/* eslint-disable object-curly-newline */
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import useRoundNumber from '@hooks/useRoundNumber';

import AlertMessage from '@ui-kit/alert/Alert';
import BaseButton from '@ui-kit/buttons/baseButton';
import BaseInput from '@ui-kit/inputs/baseInput/BaseInput';
import BaseSelect from '@ui-kit/inputs/baseSelect/BaseSelect';
import BaseTextArea from '@ui-kit/inputs/baseTextArea/BaseTextArea';
import Box from '@ui-kit/box';
import BreakLine from '@ui-kit/dividers/breakLine';
import ButtonSegment from '@ui-kit/buttons/buttonSegment/ButtonSegment';
import InputLabel from '@ui-kit/inputs/inputLabel';
import LinkButton from '@ui-kit/buttons/linkButton/LinkButton';
import ToggleSwitch from '@ui-kit/inputs/toggleSwitch/ToggleSwitch';

import BaseTypeAhead from '@distinct-components/typeAheadInputs/baseTypeAhead';

import { SOCIETIES } from '@constants/collectionSocieties';
import { useState } from 'preact/hooks';

import { Fragment } from 'preact';
import { StyledPercentBox, StyledPercentageInput, StyledDateWrapper } from './AddedContractDataEditStyles';

const WriterSocieties = SOCIETIES.filter((s) => s.societyType === 'pro');
const ipiRegex = /^[0-9]{9,11}$/;

function AddedContractDataEdit({ contractData, handleSetContractData, handleFinishEditing }) {
  const [editedContractData, setEditedContractData] = useState(contractData);
  const [writer, setWriter] = useState(contractData.parties.find((p) => !p.isPublisher));
  const [publisher, setPublisher] = useState(contractData.parties.find((p) => p.isPublisher));
  const [mdr, setMDR] = useState(contractData.deliveryCommitment);

  const [error, setError] = useState(null);
  const [showEditExtractedData, setShowEditExtractedData] = useState(false);
  const [hasEndDate, setHasEndDate] = useState(!!contractData.endDate);

  const onClickSaveEdits = () => {
    setError(null);

    if (writer.ipi && !ipiRegex.test(writer.ipi)) {
      setError("Invalid Writer's IPI");
      return;
    }
    if (publisher.ipi && !ipiRegex.test(publisher.ipi)) {
      setError("Invalid Publisher's IPI");
      return;
    }

    const { startDate, endDate } = editedContractData;
    if (hasEndDate && startDate && endDate && dayjs(startDate, 'MM/DD/YYYY').isAfter(dayjs(endDate, 'MM/DD/YYYY'))) {
      setError('Invalid Date Range');
      return;
    }

    const roundedOwnership = useRoundNumber(parseFloat(editedContractData.ownershipPercentage), 2) || null;
    if (roundedOwnership < 0 || roundedOwnership > 100) {
      setError('Invalid Ownership Percentage');
      return;
    }

    const deliveryCommitment = {
      ...mdr,
      totalCompositions: mdr.included ? (parseInt(mdr.totalCompositions, 10) || null) : null,
      qualifyingPercentage: mdr.included ? (useRoundNumber(parseFloat(mdr.qualifyingPercentage), 2) || 100) : null,
      aggregation: {
        ...mdr.aggregation,
        minimumPercentage: (mdr.included && mdr.aggregation.allowed) ? (useRoundNumber(parseFloat(mdr.aggregation.minimumPercentage), 2) || 0) : null,
      },
    };
    if (deliveryCommitment.totalCompositions <= 0) {
      setError('Invalid Number of Works for Delivery Requirement');
      return;
    }
    if (deliveryCommitment.qualifyingPercentage <= 0 || deliveryCommitment.qualifyingPercentage > 100) {
      setError('Invalid Qualifying Percentage for Delivery Requirement');
      return;
    }
    if (deliveryCommitment.aggregation.minimumPercentage < 0 || deliveryCommitment.aggregation.minimumPercentage > 100) {
      setError('Invalid Minimum Percentage for Delivery Requirement');
      return;
    }
    if (deliveryCommitment.aggregation.minimumPercentage >= deliveryCommitment.qualifyingPercentage) {
      setError('Minimum Percentage has to be less than Qualifying Percentage for Delivery Requirement');
      return;
    }

    const data = {
      ...editedContractData,
      writers: [writer, ...editedContractData.writers.slice(1)],
      publisher,
      startDate,
      endDate: hasEndDate ? endDate : null,
      ownershipPercentage: roundedOwnership,
      deliveryCommitment,
    };

    handleSetContractData(data);
    handleFinishEditing();
  };

  return (
    <Box>

      <InputLabel label="Contract Summary" />
      <BaseTextArea
        placeholder="Contract Summary"
        type="textarea"
        defaultValue={editedContractData.summary}
        onChange={(e) => setEditedContractData({ ...editedContractData, summary: e.target.value })}
        fluid
        maxLength="600"
        mb="1.5em"
        style={{ minHeight: '284px' }}
      />

      <Box>
        <InputLabel label="Reference Title" />
        <BaseInput
          id="refTitle"
          name="refTitle"
          placeholder="Title for reference..."
          defaultValue={editedContractData.name}
          onChange={(e) => setEditedContractData({ ...editedContractData, name: e.target.value })}
          type="text"
          fluid
          mb="1em"
        />
      </Box>

      <LinkButton
        type="button"
        mb="1.25rem"
        fontSize="0.813rem"
        color="var(--text-secondary)"
        btnText={showEditExtractedData ? 'Hide All Fields' : 'Show All Fields'}
        onClick={() => setShowEditExtractedData(!showEditExtractedData)}
      />

      <BreakLine mb="1.25em" color="var(--panel-info-border)" />

      {showEditExtractedData && (
        <Fragment>
          <Box>
            <InputLabel label="Writer Name" />
            <BaseInput
              id="writerName"
              name="writerName"
              placeholder="Name of the writer"
              defaultValue={writer.name}
              onChange={(e) => setWriter({ ...writer, name: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Writer Alias" />
            <BaseInput
              id="writerAlias"
              name="writerAlias"
              placeholder="Alias of the writer"
              defaultValue={writer.alias}
              onChange={(e) => setWriter({ ...writer, alias: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Writer Email" />
            <BaseInput
              id="writerEmail"
              name="writerEmail"
              placeholder="Email of the writer"
              defaultValue={writer.email}
              onChange={(e) => setWriter({ ...writer, email: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Writer IPI" />
            <BaseInput
              id="writerIPI"
              name="writerIPI"
              placeholder="IPI of the writer"
              defaultValue={writer?.ipi}
              onChange={(e) => setWriter({ ...writer, ipi: e.target.value })}
              type="text"
              inputMode="decimal"
              pattern="[0-9]+"
              maxLength="11"
              minLength="9"
              fluid
              mb="1em"
            />
          </Box>

          <BaseTypeAhead
            label="Writer PRO"
            id="writerPRO"
            data={WriterSocieties}
            nameValue="name"
            handleChange={() => {}}
            initialValue={writer.pro?.name || ''}
          />

          <Box>
            <InputLabel label="Writer Address" />
            <BaseInput
              id="writerAddress"
              name="writerAddress"
              placeholder="Address of the writer"
              defaultValue={writer.address}
              onChange={(e) => setWriter({ ...writer, address: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Writer Country" />
            <BaseInput
              id="writerCountry"
              name="writerCountry"
              placeholder="Country of the writer"
              defaultValue={writer.country}
              onChange={(e) => setWriter({ ...writer, country: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Writer Self-Owned Publisher" />
            <BaseInput
              id="writerSelfPub"
              name="writerSelfPub"
              placeholder="Self-owned publisher of the writer"
              defaultValue={writer.selfPublisher?.name}
              onChange={(e) => setWriter({ ...writer, selfPublisher: { ...writer.selfPublisher, name: e.target.value } })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <BreakLine mb="1.25em" mt="1em" color="var(--panel-info-border)" />

          <Box>
            <InputLabel label="Publisher Name" />
            <BaseInput
              id="publisherName"
              name="publisherName"
              placeholder="Name of the publisher"
              defaultValue={publisher.name}
              onChange={(e) => setPublisher({ ...publisher, name: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Publisher Email" />
            <BaseInput
              id="publisherEmail"
              name="publisherEmail"
              placeholder="Email of the publisher"
              defaultValue={publisher.email}
              onChange={(e) => setPublisher({ ...publisher, email: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Publisher IPI" />
            <BaseInput
              id="publisherIPI"
              name="publisherIPI"
              placeholder="IPI of the publisher"
              defaultValue={publisher.ipi}
              onChange={(e) => setPublisher({ ...publisher, ipi: e.target.value })}
              type="text"
              inputMode="decimal"
              pattern="[0-9]+"
              maxLength="11"
              minLength="9"
              fluid
              mb="1em"
            />
          </Box>

          <BaseTypeAhead
            label="Publisher PRO"
            id="publisherPRO"
            data={WriterSocieties}
            nameValue="name"
            handleChange={() => {}}
            initialValue={publisher.pro?.name || ''}
          />

          <Box>
            <InputLabel label="Publisher Address" />
            <BaseInput
              id="publisherAddress"
              name="publisherAddress"
              placeholder="Address of the publisher"
              defaultValue={publisher.address}
              onChange={(e) => setPublisher({ ...publisher, address: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <Box>
            <InputLabel label="Publisher Country" />
            <BaseInput
              id="publisherCountry"
              name="publisherCountry"
              placeholder="Country of the publisher"
              defaultValue={publisher.country}
              onChange={(e) => setPublisher({ ...publisher, country: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <BreakLine mb="1.25em" mt="1em" color="var(--panel-info-border)" />

          <Box>
            <InputLabel label="Deal Type" />
            <BaseSelect
              id="dealType"
              name="dealType"
              onChange={(e) => setEditedContractData({ ...editedContractData, isTermDeal: !!e.target.value })}
              fluid
              mb="1em"
            >
              <option value default={editedContractData.isTermDeal} selected={editedContractData.isTermDeal}>Term Deal</option>
              <option value="" default={!editedContractData.isTermDeal} selected={!editedContractData.isTermDeal}>Not Term Deal</option>
            </BaseSelect>
          </Box>

          <Box>
            <InputLabel label="Effective Dates" />
            <Box display="flex" mb="1em" width="100%">
              <ButtonSegment onClick={() => setHasEndDate(true)} btnText="Until a Date" variant="first" active={hasEndDate} fluid maxHeight="inherit" />
              <ButtonSegment onClick={() => setHasEndDate(false)} btnText="No End Date" variant="last" active={!hasEndDate} fluid maxHeight="inherit" />
            </Box>

            <StyledDateWrapper>
              <Box className="dateBox" width="12em">
                <InputLabel label="From Date" />
                <BaseInput
                  id="startDate"
                  name="startDate"
                  defaultValue={dayjs(editedContractData.startDate, 'MM/DD/YYYY')?.format()?.substring(0, 10)}
                  onChange={(e) => setEditedContractData({ ...editedContractData, startDate: dayjs(e.target.value).format('MM/DD/YYYY') })}
                  type="date"
                  fluid
                  mb="1em"
                />
              </Box>

              {hasEndDate && <BreakLine m="auto 1em" width="10%" color="var(--panel-info-border)" />}

              {hasEndDate && (
              // <Fragment>
                <Box className="dateBox" width="12em">
                  <InputLabel label="To Date" />
                  <BaseInput
                    id="endDate"
                    name="endDate"
                    defaultValue={dayjs(editedContractData.endDate, 'MM/DD/YYYY')?.format()?.substring(0, 10)}
                    onChange={(e) => setEditedContractData({ ...editedContractData, endDate: dayjs(e.target.value).format('MM/DD/YYYY') })}
                    type="date"
                    fluid
                    mb="1em"
                  />
                </Box>
              // </Fragment>
              )}
            </StyledDateWrapper>
          </Box>

          <Box mb="1em">
            <InputLabel label="Undivided Ownership Interest" />
            <Box display="flex" mb="0.25em">
              <StyledPercentageInput
                id="ownership"
                name="ownership"
                placeholder="Enter Percentage"
                defaultValue={editedContractData.ownershipPercentage}
                onChange={(e) => setEditedContractData({ ...editedContractData, ownershipPercentage: e.target.value })}
                type="number"
                inputMode="decimal"
                step="0.01"
                max={100}
                min={0}
                fluid
                mb="1em"
              />
              <StyledPercentBox>%</StyledPercentBox>
            </Box>
          </Box>

          <Box>
            <InputLabel label="Administration Territory" />
            <BaseInput
              id="territory"
              name="territory"
              placeholder="Territory of the contract"
              defaultValue={editedContractData.territory}
              onChange={(e) => setEditedContractData({ ...editedContractData, territory: e.target.value })}
              type="text"
              fluid
              mb="1em"
            />
          </Box>

          <BreakLine mb="1.25em" mt="1em" color="var(--panel-info-border)" />

          <Box display="flex" justifyContent="space-between" mb="1em">
            <InputLabel label="Include Delivery Requirement" />
            <ToggleSwitch id="commitment">
              <input
                id="commitment"
                name="commitment"
                type="checkbox"
                checked={mdr.included}
                onChange={() => setMDR({ ...mdr, included: !mdr.included })}
              />
            </ToggleSwitch>
          </Box>

          {mdr.included && (
          <Box>
            <Box>
              <InputLabel label="Works to Deliver" />
              <BaseInput
                id="worksToDeliver"
                name="worksToDeliver"
                placeholder="Number of works to deliver"
                defaultValue={mdr.totalCompositions}
                onChange={(e) => setMDR({ ...mdr, totalCompositions: e.target.value })}
                type="number"
                inputMode="numeric"
                max={1000000}
                min={1}
                fluid
                mb="1em"
              />
            </Box>

            <Box mb="1em">
              <InputLabel label="Percentage Required Per Delivery" />
              <Box display="flex" mb="0.25em">
                <StyledPercentageInput
                  placeholder="Enter Percentage"
                  defaultValue={mdr.qualifyingPercentage || 100}
                  onChange={(e) => setMDR({ ...mdr, qualifyingPercentage: e.target.value })}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  max={100}
                  min={0.01}
                  fluid
                  mb="1em"
                />
                <StyledPercentBox>%</StyledPercentBox>
              </Box>
            </Box>

            <Box display="flex" justifyContent="space-between" mb="1em">
              <InputLabel label="Allow Combining Ownership Percentages" />
              <ToggleSwitch id="aggregation">
                <input
                  id="aggregation"
                  name="aggregation"
                  type="checkbox"
                  checked={!!mdr.aggregation.allowed}
                  onChange={() => setMDR({ ...mdr, aggregation: { ...mdr.aggregation, allowed: !mdr.aggregation.allowed } })}
                />
              </ToggleSwitch>
            </Box>

            {mdr.aggregation.allowed && (
            <Box mb="1em">
              <InputLabel label="Minimum Percentage Per Work to Combine" />
              <Box display="flex" mb="0.25em">
                <StyledPercentageInput
                  placeholder="Enter Percentage"
                  defaultValue={mdr.aggregation.minimumPercentage}
                  onChange={(e) => setMDR({ ...mdr, aggregation: { ...mdr.aggregation, minimumPercentage: e.target.value } })}
                  type="number"
                  inputMode="decimal"
                  // step="0.01"
                  // max={99.99}
                  // min={0}
                  fluid
                  mb="1em"
                />
                <StyledPercentBox>%</StyledPercentBox>
              </Box>
            </Box>
            )}
          </Box>
          )}

          <BreakLine mb="1.25em" mt="1em" color="var(--panel-info-border)" />
        </Fragment>
      )}

      {error && (
        <AlertMessage
          variant="negative"
          message={error}
          mb="1.5em"
          mt="1.5em"
        />
      )}

      {contractData && (
        <BaseButton
          mb="1.5em"
          btnText="Save and Review"
          fluid
          // isLoading={isLoading}
          onClick={onClickSaveEdits}
        />
      )}

    </Box>
  );
}

AddedContractDataEdit.propTypes = {
  contractData: PropTypes.object.isRequired,
  handleSetContractData: PropTypes.func.isRequired,
  handleFinishEditing: PropTypes.func.isRequired,
};

export default AddedContractDataEdit;
